import { jsonApi } from "@spree/storefront-api-v2-sdk";
import { requireConfigValue } from "../../isomorphic-config";
import createGetAbsoluteImageUrl from "../create-get-absolute-image-url";
import expandOptions from "../expand-options";
import getMediaGallery from "../get-media-gallery";
import getProductPath from "../get-product-path";
import MissingPrimaryVariantError from "../../errors/MissingPrimaryVariantError";
import MissingOptionValueError from "../../errors/MissingOptionValueError";
const placeholderImage = requireConfigValue("productPlaceholderImageUrl");
const imagesOptionFilter = requireConfigValue("imagesOptionFilter");
const normalizeProduct = (spreeSuccessResponse, spreeProduct)=>{
    const spreePrimaryVariant = jsonApi.findSingleRelationshipDocument(spreeSuccessResponse, spreeProduct, "primary_variant");
    if (spreePrimaryVariant === null) {
        throw new MissingPrimaryVariantError(`Couldn't find primary variant for product with id ${spreeProduct.id}.`);
    }
    const sku = spreePrimaryVariant.attributes.sku;
    const price = {
        value: parseFloat(spreeProduct.attributes.price),
        currencyCode: spreeProduct.attributes.currency
    };
    const spreeProductProperties = jsonApi.findRelationshipDocuments(spreeSuccessResponse, spreeProduct, "product_properties");
    let properties;
    if (spreeProductProperties.length > 0) {
        properties = spreeProductProperties.map((property)=>{
            return {
                id: property.id,
                name: property.attributes.name,
                value: property.attributes.value
            };
        });
    } else {
        properties = [];
    }
    const hasNonMasterVariants = spreeProduct.relationships.variants.data.length >= 1;
    const showOptions = requireConfigValue("showSingleVariantOptions") || hasNonMasterVariants;
    let options = [];
    const spreeVariantRecords = jsonApi.findRelationshipDocuments(spreeSuccessResponse, spreeProduct, "variants");
    // Use variants with option values if available. Fall back to
    // Spree primary_variant if no explicit variants are present.
    const spreeOptionsVariantsOrPrimary = spreeVariantRecords.length === 0 ? [
        spreePrimaryVariant
    ] : spreeVariantRecords;
    const variants = spreeOptionsVariantsOrPrimary//.filter((spreeVariantRecord) => spreeVariantRecord.attributes.available)
    .map((spreeVariantRecord)=>{
        var ref, ref1;
        let variantOptions = [];
        if (showOptions) {
            const spreeOptionValues = jsonApi.findRelationshipDocuments(spreeSuccessResponse, spreeVariantRecord, "option_values");
            // Only include options which are used by variants.
            spreeOptionValues.forEach((spreeOptionValue)=>{
                variantOptions = expandOptions(spreeSuccessResponse, spreeOptionValue, variantOptions);
                options = expandOptions(spreeSuccessResponse, spreeOptionValue, options);
            });
        }
        const variantImages = getMediaGallery(jsonApi.findRelationshipDocuments(spreeSuccessResponse, spreeVariantRecord, "images"), createGetAbsoluteImageUrl(requireConfigValue("imageHost")));
        let images;
        if (variantImages.length === 0) {
            // Show primary variant images
            const spreePrimaryVariantImageRecords = jsonApi.findRelationshipDocuments(spreeSuccessResponse, spreePrimaryVariant, "images");
            const primaryVariantImages = getMediaGallery(spreePrimaryVariantImageRecords, createGetAbsoluteImageUrl(requireConfigValue("imageHost")));
            if (primaryVariantImages.length === 0) {
                images = placeholderImage === false ? [] : [
                    {
                        url: placeholderImage
                    }
                ];
            } else {
                images = primaryVariantImages;
            }
        } else {
            images = variantImages;
        }
        // If variant has a 'metal' option value, add it to the slug.
        const metalOption = (ref1 = (ref = variantOptions.find((opt)=>opt.displayName.toLowerCase() === "metal")) == null ? void 0 : ref.values[0]) == null ? void 0 : ref1.label.toLowerCase();
        // E.g.: If variant has 'White Gold 14K' metal option value, the slug should be '/[product-slug]-white-gold-14k'.
        // Otherwise return the product slug only.
        const variantSlug = metalOption ? `${spreeProduct.attributes.slug}-${metalOption.replace(/\s/g, "-")}` : spreeProduct.attributes.slug;
        const variantPrice = {
            value: parseFloat(spreeVariantRecord.attributes.price),
            currencyCode: spreeProduct.attributes.currency
        };
        // If necessary, you can return more variant's data and add the corresponding attributes to the ProductVariant model.
        return {
            id: spreeVariantRecord.id,
            name: spreeProduct.attributes.name,
            sku: spreeVariantRecord.attributes.sku,
            slug: variantSlug,
            images,
            price: variantPrice,
            options: variantOptions,
            isMaster: spreeVariantRecord.attributes.is_master,
            weight: spreeVariantRecord.attributes.weight,
            height: spreeVariantRecord.attributes.height,
            width: spreeVariantRecord.attributes.width,
            depth: spreeVariantRecord.attributes.depth,
            productId: spreeProduct.id,
            properties,
            videos: spreeVariantRecord.attributes.videos,
            sizes: spreeVariantRecord.attributes.sizes,
            description: spreeProduct.attributes.description,
            cyoType: spreeProduct.attributes.cyo_type,
            availableForSale: spreeVariantRecord.attributes.available
        };
    });
    const spreePrimaryVariantImageRecords = jsonApi.findRelationshipDocuments(spreeSuccessResponse, spreePrimaryVariant, "images");
    let spreeVariantImageRecords;
    if (imagesOptionFilter === false) {
        spreeVariantImageRecords = spreeVariantRecords.reduce((accumulatedImageRecords, spreeVariantRecord)=>{
            return [
                ...accumulatedImageRecords,
                ...jsonApi.findRelationshipDocuments(spreeSuccessResponse, spreeVariantRecord, "images"), 
            ];
        }, []);
    } else {
        const spreeOptionTypes = jsonApi.findRelationshipDocuments(spreeSuccessResponse, spreeProduct, "option_types");
        const imagesFilterOptionType = spreeOptionTypes.find((spreeOptionType)=>spreeOptionType.attributes.name === imagesOptionFilter);
        if (!imagesFilterOptionType) {
            console.warn(`Couldn't find option type having name ${imagesOptionFilter} for product with id ${spreeProduct.id}.` + " Showing no images for this product.");
            spreeVariantImageRecords = [];
        } else {
            const imagesOptionTypeFilterId = imagesFilterOptionType.id;
            const includedOptionValuesImagesIds = [];
            spreeVariantImageRecords = spreeVariantRecords.reduce((accumulatedImageRecords, spreeVariantRecord)=>{
                const spreeVariantOptionValuesIdentifiers = spreeVariantRecord.relationships.option_values.data;
                const spreeOptionValueOfFilterTypeIdentifier = spreeVariantOptionValuesIdentifiers.find((spreeVariantOptionValuesIdentifier)=>imagesFilterOptionType.relationships.option_values.data.some((filterOptionTypeValueIdentifier)=>filterOptionTypeValueIdentifier.id === spreeVariantOptionValuesIdentifier.id));
                if (!spreeOptionValueOfFilterTypeIdentifier) {
                    throw new MissingOptionValueError(`Couldn't find option value related to option type with id ${imagesOptionTypeFilterId}.`);
                }
                const optionValueImagesAlreadyIncluded = includedOptionValuesImagesIds.includes(spreeOptionValueOfFilterTypeIdentifier.id);
                if (optionValueImagesAlreadyIncluded) {
                    return accumulatedImageRecords;
                }
                includedOptionValuesImagesIds.push(spreeOptionValueOfFilterTypeIdentifier.id);
                return [
                    ...accumulatedImageRecords,
                    ...jsonApi.findRelationshipDocuments(spreeSuccessResponse, spreeVariantRecord, "images"), 
                ];
            }, []);
        }
    }
    const spreeImageRecords = [
        ...spreePrimaryVariantImageRecords,
        ...spreeVariantImageRecords, 
    ];
    const productImages = getMediaGallery(spreeImageRecords, createGetAbsoluteImageUrl(requireConfigValue("imageHost")));
    const images = productImages.length === 0 ? placeholderImage === false ? [] : [
        {
            url: placeholderImage
        }
    ] : productImages;
    const slug = spreeProduct.attributes.slug;
    const path = getProductPath(spreeProduct);
    const spreeTaxonRecords = jsonApi.findRelationshipDocuments(spreeSuccessResponse, spreeProduct, "taxons");
    const taxons = spreeTaxonRecords.map((taxon)=>{
        return {
            id: taxon.id,
            name: taxon.attributes.name,
            permalink: taxon.attributes.permalink,
            isLeaf: taxon.attributes.is_leaf
        };
    });
    return {
        id: spreeProduct.id,
        name: spreeProduct.attributes.name,
        description: spreeProduct.attributes.description,
        images,
        variants,
        options,
        price,
        slug,
        path,
        sku,
        cyo_type: spreeProduct.attributes.cyo_type || null,
        taxons,
        properties,
        available: spreeProduct.attributes.available,
        canBeSetWith: spreeProduct.attributes.can_set,
        productType: spreeProduct.attributes.product_type,
        ringSizes: spreeProduct.attributes.ring_sizes,
        status: spreeProduct.attributes.status || null
    };
};
export default normalizeProduct;
